/**
 * API Client fuer Channel Readiness
 *
 * Endpoints fuer Text-Regeln und Readiness Dashboard.
 */

import apiClient from '../api-client'
import type { ImageIntelligenceCounts } from './image-intelligence'
import type { ReadinessActionState } from '@/types/products'

const BASE_URL = '/api/v1'

// =============================================================================
// TEXT RULES
// =============================================================================

export interface TextRule {
  id: string
  workspace_id: string
  channel_id: string
  attribute_code: string
  rule_type: string
  rule_config: { value: number | boolean }
  error_message?: Record<string, string>
  scope: string
  priority: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface TextRulesResponse {
  success: boolean
  rules: TextRule[]
  error?: string
}

export interface TextRuleResponse {
  success: boolean
  rule: TextRule
  error?: string
}

export async function getTextRules(channelId: string): Promise<TextRulesResponse> {
  const response = await apiClient.get<TextRulesResponse>(
    `${BASE_URL}/channels/${channelId}/text-rules`
  )
  return response.data
}

export async function getAllTextRules(): Promise<TextRulesResponse> {
  const response = await apiClient.get<TextRulesResponse>(
    `${BASE_URL}/channels/text-rules`
  )
  return response.data
}

export async function saveTextRule(
  channelId: string,
  rule: {
    attribute_code: string
    rule_type: string
    rule_config: { value: number | boolean }
    error_message?: Record<string, string>
  }
): Promise<TextRuleResponse> {
  const response = await apiClient.post<TextRuleResponse>(
    `${BASE_URL}/channels/${channelId}/text-rules`,
    rule
  )
  return response.data
}

export async function saveTextRulesBulk(
  channelId: string,
  rules: Array<{
    attribute_code: string
    rule_type: string
    rule_config: { value: number | boolean }
    error_message?: Record<string, string>
  }>
): Promise<TextRulesResponse> {
  const response = await apiClient.post<TextRulesResponse>(
    `${BASE_URL}/channels/${channelId}/text-rules/bulk`,
    { rules }
  )
  return response.data
}

export async function deleteTextRule(
  channelId: string,
  ruleId: string
): Promise<{ success: boolean }> {
  const response = await apiClient.delete<{ success: boolean }>(
    `${BASE_URL}/channels/${channelId}/text-rules/${ruleId}`
  )
  return response.data
}

// =============================================================================
// READINESS DASHBOARD
// =============================================================================

export interface ReadinessSummaryItem {
  channel_id: string
  channel_name: string
  channel_type?: number
  total: number
  ready: number
  warning: number
  error: number
  avg_score: number
  image_rule_count: number
  text_rule_count: number
}

export interface ReadinessScore {
  id: string
  product_id: string
  channel_id: string
  readiness_score: number
  image_rule_score: number
  text_rule_score: number
  completeness_score: number
  issues: Array<{
    issue_type: string
    field: string
    message: string
    severity: string
    expected?: string
    actual?: string
  }>
  issue_count: number
  stale: boolean
  computed_at: string
  product?: {
    id: string
    name: string
    sku: string | null
    image_url: string | null
    first_image_url: string | null
    ai_ref_url: string | null
    quantity_30d: number | null
    revenue_30d: number | null
    quantity_90d: number | null
    revenue_90d: number | null
  }
}

export interface IssueGroup {
  issue_type: string
  field: string
  message: string
  product_count: number
}

export interface IssueFilter {
  issue_type: string
  field: string
  message: string
  missing_packshot?: boolean
}

export async function getReadinessSummary(): Promise<{
  success: boolean
  summary: ReadinessSummaryItem[]
}> {
  const response = await apiClient.get(`${BASE_URL}/readiness/summary`)
  return response.data
}

export async function getReadinessProducts(
  channelId: string,
  params?: {
    page?: number
    page_size?: number
    min_score?: number
    max_score?: number
    search?: string
    issue_type?: string
    issue_field?: string
    issue_message?: string
    missing_packshot?: boolean
    image_status?: string
    sort_by?: string
    sort_dir?: string
  }
): Promise<{
  success: boolean
  scores: ReadinessScore[]
  total: number
  page: number
  page_size: number
}> {
  const response = await apiClient.get(
    `${BASE_URL}/readiness/channels/${channelId}/products`,
    { params }
  )
  return response.data
}

export type { ImageIntelligenceCounts } from './image-intelligence'

export async function getReadinessIssues(channelId: string): Promise<{
  success: boolean
  issues: IssueGroup[]
  missing_packshot_count: number
  image_intelligence?: ImageIntelligenceCounts
}> {
  const response = await apiClient.get(
    `${BASE_URL}/readiness/channels/${channelId}/issues`
  )
  return response.data
}

export async function getProductReadiness(productId: string): Promise<{
  success: boolean
  scores: ReadinessScore[]
}> {
  const response = await apiClient.get(
    `${BASE_URL}/readiness/products/${productId}`
  )
  return response.data
}

export async function triggerRecompute(params?: {
  channel_id?: string
  limit?: number
}): Promise<{
  success: boolean
  message: string
  computed: number
}> {
  const response = await apiClient.post(`${BASE_URL}/readiness/recompute`, params)
  return response.data
}

// =============================================================================
// S8: Workspace-Recompute (Hybrid C Button) + per-Produkt-Issues
// =============================================================================

export type IssueSeverity = 'error' | 'warning' | 'info'

export interface ProductIssue {
  issue_type: string
  field: string
  message: string
  severity?: IssueSeverity
  actual?: string | null
  expected?: string | null
}

export interface ProductWithIssues {
  product_id: string
  channel_id: string | null
  name: string | null
  sku?: string | null
  thumbnail_url?: string | null
  channels?: string[]
  readiness_action_state?: ReadinessActionState
  issue_count: number
  issues: ProductIssue[]
}

export async function recomputeWorkspace(params?: { limit?: number }): Promise<{
  success: boolean
  computed: number
  stale_remaining: number
}> {
  const response = await apiClient.post(`${BASE_URL}/readiness/recompute-workspace`, params ?? {})
  return response.data
}

export async function getProductsWithIssues(params?: {
  page?: number
  page_size?: number
}): Promise<{ success: boolean; products: ProductWithIssues[]; total: number }> {
  const response = await apiClient.get(`${BASE_URL}/products/issues`, { params })
  return response.data
}
